'use client'

import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type Node,
  type Edge,
  type OnEdgesChange,
} from '@xyflow/react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/use-auth'
import { FlowToolbar } from './FlowToolbar'
import { FlowCanvas } from './FlowCanvas'
import { NodeConfigPanel } from './panels/NodeConfigPanel'
import type { CallFlow, FlowNode, FlowEdge, EdgeType } from '@/lib/pbx/flow/types'
import { useCallFlowMutations } from '@/hooks/pbx/use-call-flows'
import { useTranslations } from "next-intl"


interface FlowEditorProps {
  flow: CallFlow
  onFlowChange?: (flow: CallFlow) => void
}

// Edge data stored in React Flow edge
interface ReactFlowEdgeData extends Record<string, unknown> {
  edgeType: EdgeType
}

// Convert FlowEdge (DB format) to React Flow Edge
const toReactFlowEdge = (edge: FlowEdge): Edge<ReactFlowEdgeData> => ({
  id: edge.id,
  source: edge.source,
  target: edge.target,
  sourceHandle: edge.sourceHandle,
  type: 'deletable', // React Flow component type
  data: { edgeType: edge.type }, // Our business logic type
})

// Convert React Flow Edge back to FlowEdge (DB format)
const toFlowEdge = (edge: Edge<ReactFlowEdgeData>): FlowEdge => ({
  id: edge.id,
  source: edge.source,
  target: edge.target,
  sourceHandle: edge.sourceHandle ?? undefined,
  type: edge.data?.edgeType || 'default', // Restore business logic type
})

// Determine edge type based on source handle
const getEdgeType = (connection: Connection): EdgeType => {
  if (connection.sourceHandle) {
    return connection.sourceHandle as EdgeType
  }
  return 'default'
}

function FlowEditorInner({ flow, onFlowChange }: FlowEditorProps) {
  const tUi = useTranslations("Pbx.PbxFlowEditorFlowEditor")

  const { fetchWithAuth } = useAuth()
  const { updateFlow, syncFlow, validateFlow, isLoading } = useCallFlowMutations()

  // Convert DB edges to React Flow format on init
  const initialEdges = useMemo(
    () => flow.edges.map(toReactFlowEdge),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Only on initial mount
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(flow.nodes as Node[])
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null)
  const [isSynced, setIsSynced] = useState(!!flow.synced_at)
  const [isActive, setIsActive] = useState(flow.is_active)
  const [isTogglingActive, setIsTogglingActive] = useState(false)
  const [assignedPhones, setAssignedPhones] = useState<string[]>([])

  // Load assigned phone numbers
  useEffect(() => {
    const loadAssignedPhones = async () => {
      try {
        const res = await fetchWithAuth(`/api/pbx/flows/${flow.id}/phones`)
        if (res.ok) {
          const data = await res.json()
          setAssignedPhones(data.phones?.map((p: { phone_number: string }) => p.phone_number) || [])
        }
      } catch {
        // Ignore errors, just don't show phones
      }
    }
    loadAssignedPhones()
  }, [flow.id, fetchWithAuth])

  const handleConnect = useCallback(
    (connection: Connection) => {
      const edgeType = getEdgeType(connection)
      const newEdge: Edge<ReactFlowEdgeData> = {
        id: `edge-${Date.now()}`,
        source: connection.source!,
        target: connection.target!,
        sourceHandle: connection.sourceHandle || undefined,
        type: 'deletable', // React Flow component type
        data: { edgeType }, // Business logic type
      }
      setEdges((eds) => addEdge(newEdge, eds))
    },
    [setEdges]
  )

  const handleNodesAdd = useCallback(
    (newNodes: FlowNode[]) => {
      setNodes((nds: Node[]) => [...nds, ...newNodes as Node[]])
    },
    [setNodes]
  )

  const handleNodeSelect = useCallback((node: FlowNode | null) => {
    setSelectedNode(node)
  }, [])

  const handleNodeUpdate = useCallback(
    (nodeId: string, data: Partial<FlowNode['data']>) => {
      setNodes((nds: Node[]) =>
        nds.map((node: Node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...data } }
            : node
        )
      )
      if (selectedNode?.id === nodeId) {
        setSelectedNode((prev) =>
          prev ? { ...prev, data: { ...prev.data, ...data } } : null
        )
      }
    },
    [setNodes, selectedNode]
  )

  const handleToggleActive = useCallback(async () => {
    const newActiveState = !isActive
    setIsTogglingActive(true)

    try {
      // Validate before activating
      if (newActiveState) {
        const result = await validateFlow(flow.id)
        if (!result.valid) {
          toast.error(tUi("cannotActivate"), {
            description: `Flow has validation errors: ${result.errors.map((e: { message: string }) => e.message).join(', ')}`,
          })
          return
        }
      }

      await updateFlow(flow.id, { is_active: newActiveState })
      setIsActive(newActiveState)

      // Auto-sync when activating (only if phones are assigned)
      if (newActiveState && assignedPhones.length > 0) {
        try {
          const syncResult = await syncFlow(flow.id)
          if (syncResult?.synced) {
            setIsSynced(true)
          }
        } catch {
          // Sync failure is not critical, show warning
          toast.warning(tUi("warning"), {
            description: tUi("flowActivatedButSyncFailedPleaseSyncManually"),
          })
        }
      }

      toast.success(newActiveState ? 'Flow Activated' : 'Flow Deactivated', {
        description: newActiveState
          ? 'Flow is now active and can be used with phone numbers.'
          : 'Flow is now inactive and cannot be assigned to phone numbers.',
      })

      onFlowChange?.({ ...flow, is_active: newActiveState })
    } catch (error) {
      toast.error(tUi("error"), {
        description: error instanceof Error ? error.message : 'Failed to toggle flow status',
      })
    } finally {
      setIsTogglingActive(false)
    }
  }, [isActive, updateFlow, flow, assignedPhones.length, onFlowChange, validateFlow, tUi, syncFlow])

  const handleSave = useCallback(async () => {
    try {
      // Convert React Flow edges back to DB format
      const flowEdges = edges.map((e) => toFlowEdge(e as Edge<ReactFlowEdgeData>))

      console.log('[FlowEditor.handleSave] Saving flow with:', {
        nodesCount: nodes.length,
        edgesCount: flowEdges.length,
        edges: flowEdges.map(e => ({ id: e.id, source: e.source, target: e.target, type: e.type })),
      })

      const result = await updateFlow(flow.id, {
        nodes: nodes as FlowNode[],
        edges: flowEdges,
      })

      // Check if auto-sync happened
      if (result?.synced) {
        setIsSynced(true)
        toast.success(tUi("savedSynced"), {
          description: tUi("flowHasBeenSavedAndSyncedToTwilio"),
        })
      } else if (result?.syncError) {
        toast.error(tUi("savedSyncFailed"), {
          description: `Flow saved, but sync failed: ${result.syncError}`,
        })
      } else {
        toast.success(tUi("saved"), {
          description: tUi("flowHasBeenSavedSuccessfully"),
        })
      }
    } catch (error) {
      console.error('[FlowEditor.handleSave] Error saving flow:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save flow'
      toast.error(tUi("saveError"), {
        description: errorMessage,
        duration: 10000, // Show error for 10 seconds
      })
    }
  }, [edges, nodes, updateFlow, flow.id, tUi])

  const handleUpdateDetails = useCallback(
    async (name: string, description: string) => {
      try {
        await updateFlow(flow.id, { name, description })
        toast.success(tUi("updated"), {
          description: tUi("flowDetailsHaveBeenUpdated"),
        })
        onFlowChange?.({ ...flow, name, description })
      } catch (error) {
        toast.error(tUi("error"), {
          description: error instanceof Error ? error.message : 'Failed to update flow details',
        })
      }
    },
    [updateFlow, flow, tUi, onFlowChange]
  )

  return (
    <div className="flex min-h-0 min-w-0 flex-col md:h-full">
      <FlowToolbar
        flowName={flow.name}
        flowDescription={flow.description}
        isActive={isActive}
        isSaving={isLoading}
        isTogglingActive={isTogglingActive}
        isSynced={isSynced}
        syncError={flow.sync_error}
        assignedPhones={assignedPhones}
        onSave={handleSave}
        onToggleActive={handleToggleActive}
        onUpdateDetails={handleUpdateDetails}
      />
      <div className="relative flex h-[65svh] min-w-0 shrink-0 overflow-hidden md:h-auto md:min-h-0 md:flex-1">
        <FlowCanvas
          nodes={nodes as FlowNode[]}
          edges={edges as FlowEdge[]}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange as OnEdgesChange}
          onConnect={handleConnect}
          onNodeSelect={handleNodeSelect}
          onNodesAdd={handleNodesAdd}
        />
        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={(data) => handleNodeUpdate(selectedNode.id, data)}
            onClose={() => setSelectedNode(null)}
          />
        )}
      </div>
    </div>
  )
}

export function FlowEditor(props: FlowEditorProps) {
  return (
    <ReactFlowProvider>
      <FlowEditorInner {...props} />
    </ReactFlowProvider>
  )
}
